import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Search, ListChecks, MessageCircle, Star, Clock, Sparkles, Copy, RefreshCw, Users, ArrowRightLeft, FileText } from 'lucide-react';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { ActionButton, Button } from '../../components/UI';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

type Outcome = 'vip' | 'qualified' | 'nurture';
type LeadStatus = 'new' | 'contacted' | 'converted' | 'lost';

interface QuizLead {
  _id: string;
  landingPage?: { internalName: string; slug: string } | null;
  landingPageSlugSnapshot: string;
  name: string;
  whatsapp: string;
  email: string;
  lang: string;
  answers: { questionKey: string; questionText: string; optionLabel: string }[];
  outcome: Outcome;
  status: LeadStatus;
  sdrDueAt?: string | null;
  notes: string;
  utm?: { source?: string; campaign?: string };
  nurture?: { step: number; completedAt?: string | null; unsubscribedAt?: string | null; lastSentAt?: string | null };
  groupJoin?: { token?: string; outcome?: '' | Outcome; clickedAt?: string | null; clicks?: number };
  outcomeHistory?: { from: Outcome; to: Outcome; at: string }[];
  firstMessage?: { text: string; status: '' | 'pending' | 'ready' | 'failed'; generatedAt?: string | null; error?: string };
  createdAt: string;
}

const OUTCOME_BADGE: Record<Outcome, { label: string; cls: string }> = {
  vip: { label: 'VIP', cls: 'bg-amber-100 text-amber-800' },
  qualified: { label: 'Qualificado', cls: 'bg-green-100 text-green-800' },
  nurture: { label: 'Nutrição', cls: 'bg-blue-100 text-blue-800' }
};

interface GroupStats {
  total: number;
  withGroupLink: number;
  joinedGroup: number;
  withSla: number;
  contactedInSla: number;
  overdue: number;
  converted: number;
  promoted: number;
}

const pct = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—');

const STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'Novo',
  contacted: 'Contatado',
  converted: 'Convertido',
  lost: 'Perdido'
};

const formatDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

// "vence em 5 h" / "atrasado há 3 h" — leads ainda não contatados com prazo (SLA do grupo).
function sdrDeadline(lead: QuizLead): { text: string; overdue: boolean } | null {
  if (lead.status !== 'new' || !lead.sdrDueAt) return null;
  const diffH = Math.round((new Date(lead.sdrDueAt).getTime() - Date.now()) / 3600000);
  if (diffH < 0) return { text: `Atrasado há ${Math.abs(diffH)} h`, overdue: true };
  return { text: diffH === 0 ? 'Vence agora' : `Vence em ${diffH} h`, overdue: false };
}

// wa.me com a mensagem já preenchida — o SDR só revisa e aperta enviar no WhatsApp.
function waLink(digits: string, text?: string) {
  return text?.trim() ? `https://wa.me/${digits}?text=${encodeURIComponent(text.trim())}` : `https://wa.me/${digits}`;
}

// Leads antigos podem ter o número cru (sem DDI); nesse caso mostra como foi digitado.
const formatPhone = (raw: string) => parsePhoneNumberFromString(raw)?.formatInternational() || raw;

function nurtureLabel(lead: QuizLead) {
  if (lead.outcome !== 'nurture' || !lead.nurture) return null;
  if (lead.nurture.unsubscribedAt) return 'Cancelou a inscrição';
  if (lead.nurture.completedAt) return `Sequência concluída (${lead.nurture.step} e-mail(s))`;
  return `${lead.nurture.step} e-mail(s) enviado(s)`;
}

const AdminLandingQuizLeads: React.FC = () => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState<QuizLead[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});
  const [messageDraft, setMessageDraft] = useState<Record<string, string>>({});
  const [generating, setGenerating] = useState<string | null>(null);
  const [moving, setMoving] = useState<string | null>(null);
  const [stats, setStats] = useState<Record<Outcome, GroupStats> | null>(null);

  const outcome = searchParams.get('outcome') || '';
  const status = searchParams.get('status') || '';
  const overdue = searchParams.get('overdue') === '1';
  const notJoined = searchParams.get('notJoined') === '1';

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: '100',
        ...(searchTerm && { search: searchTerm }),
        ...(outcome && { outcome }),
        ...(status && { status }),
        ...(overdue && { overdue: '1' }),
        ...(notJoined && { notJoined: '1' })
      });
      const response = await axios.get(`/landing-pages/quiz-leads?${params}`);
      setLeads(response.data.leads);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Erro ao carregar leads do quiz:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, outcome, status, overdue, notJoined]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get('/landing-pages/quiz-leads/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Erro ao carregar métricas dos leads:', error);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const updateLead = async (lead: QuizLead, patch: Partial<Pick<QuizLead, 'status' | 'notes'>>) => {
    try {
      const res = await axios.put(`/landing-pages/quiz-leads/${lead._id}`, patch);
      setLeads((prev) => prev.map((l) => (l._id === lead._id ? res.data : l)));
      notifications.showSuccess('Lead atualizado', patch.status ? `Status: ${STATUS_LABELS[patch.status]}` : 'Notas salvas.');
      if (patch.status) fetchStats();
    } catch (error: any) {
      notifications.showError('Erro ao atualizar', error.response?.data?.message || 'Tente novamente.');
    }
  };

  const messageFor = (lead: QuizLead) => messageDraft[lead._id] ?? lead.firstMessage?.text ?? '';

  // Abrir o WhatsApp conta como primeiro contato: lead "novo" passa para "contatado".
  const markContacted = (lead: QuizLead) => {
    if (lead.status === 'new') updateLead(lead, { status: 'contacted' });
  };

  const generateMessage = async (lead: QuizLead) => {
    try {
      setGenerating(lead._id);
      const res = await axios.post(`/landing-pages/quiz-leads/${lead._id}/first-message`);
      setLeads((prev) => prev.map((l) => (l._id === lead._id ? res.data : l)));
      setMessageDraft((prev) => {
        const next = { ...prev };
        delete next[lead._id];
        return next;
      });
    } catch (error: any) {
      if (error.response?.data?.lead) {
        setLeads((prev) => prev.map((l) => (l._id === lead._id ? error.response.data.lead : l)));
      }
      notifications.showError('Erro ao gerar mensagem', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setGenerating(null);
    }
  };

  const { copy } = useCopyToClipboard();
  const copyMessage = (lead: QuizLead) => copy(messageFor(lead), lead._id, { message: lead.name });

  // Mover de grupo: link novo, prazo novo e primeira mensagem regenerada (já com o link do grupo).
  const moveLead = async (lead: QuizLead, to: Outcome) => {
    const ok = await confirm({
      message: `Mover ${lead.name} de ${OUTCOME_BADGE[lead.outcome].label} para ${OUTCOME_BADGE[to].label}? `
        + 'O lead recebe o link do novo grupo e volta para "Novo"'
        + (lead.outcome === 'nurture' ? ', e os e-mails de nutrição param.' : '.'),
      confirmText: 'Mover'
    });
    if (!ok) return;
    try {
      setMoving(lead._id);
      const res = await axios.put(`/landing-pages/quiz-leads/${lead._id}/outcome`, { outcome: to });
      setLeads((prev) => prev.map((l) => (l._id === lead._id ? res.data : l)));
      setMessageDraft((prev) => {
        const next = { ...prev };
        delete next[lead._id];
        return next;
      });
      setExpanded(lead._id);
      fetchStats();
      notifications.showSuccess('Lead movido', `${lead.name} → ${OUTCOME_BADGE[to].label}. Envie a nova mensagem pelo WhatsApp.`);
    } catch (error: any) {
      notifications.showError('Erro ao mover lead', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setMoving(null);
    }
  };

  // Abre o formulário de novo orçamento já com os dados de contato e a origem do lead.
  const createQuote = (lead: QuizLead) => {
    navigate('/admin/orcamento', {
      state: {
        prefill: {
          nomeCliente: lead.name,
          emailCliente: lead.email,
          telefoneCliente: formatPhone(lead.whatsapp),
          origemCliente: 'landing_page',
          origemDetalhe: [`/l/${lead.landingPage?.slug || lead.landingPageSlugSnapshot}`, lead.utm?.campaign].filter(Boolean).join(' · '),
          idioma: ['pt', 'en', 'es'].includes(lead.lang) ? lead.lang : 'pt'
        }
      }
    });
  };

  const deleteLead = async (lead: QuizLead) => {
    const ok = await confirm({
      message: `Excluir o lead de ${lead.name}? Esta ação não pode ser desfeita.`,
      confirmText: 'Excluir',
      variant: 'danger'
    });
    if (!ok) return;
    try {
      await axios.delete(`/landing-pages/quiz-leads/${lead._id}`);
      setLeads((prev) => prev.filter((l) => l._id !== lead._id));
      setTotal((prev) => Math.max(0, prev - 1));
      notifications.showSuccess('Lead excluído', lead.name);
    } catch (error: any) {
      notifications.showError('Erro ao excluir', error.response?.data?.message || 'Tente novamente.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{t('admin.quizLeads')}</h1>
          <p className="text-gray-600">{t('admin.quizLeadsDesc')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            {(['vip', 'qualified', 'nurture'] as Outcome[]).map((o) => {
              const st = stats[o];
              return (
                <div key={o} className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${OUTCOME_BADGE[o].cls}`}>{OUTCOME_BADGE[o].label}</span>
                    <span className="text-2xl font-bold text-gray-900">{st.total}</span>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
                    <dt className="text-gray-500">Entrou no grupo</dt>
                    <dd className="text-right font-medium text-gray-900">{pct(st.joinedGroup, st.withGroupLink)}</dd>
                    <dt className="text-gray-500">Contato no prazo</dt>
                    <dd className="text-right font-medium text-gray-900">{pct(st.contactedInSla, st.withSla)}</dd>
                    <dt className="text-gray-500">Atrasados agora</dt>
                    <dd className={`text-right font-medium ${st.overdue > 0 ? 'text-red-600' : 'text-gray-900'}`}>{st.overdue}</dd>
                    <dt className="text-gray-500">Convertidos</dt>
                    <dd className="text-right font-medium text-gray-900">{pct(st.converted, st.total)}</dd>
                    <dt className="text-gray-500">Mudaram de grupo</dt>
                    <dd className="text-right font-medium text-gray-900">{st.promoted}</dd>
                  </dl>
                </div>
              );
            })}
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6 p-4 sm:p-6">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Nome, e-mail ou WhatsApp"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="field pl-10 pr-4 lg:py-2"
              />
            </div>
            <select value={outcome} onChange={(e) => setFilter('outcome', e.target.value)} className="field field--select field--lg lg:w-44 lg:py-2">
              <option value="">Todos os resultados</option>
              <option value="vip">VIP</option>
              <option value="qualified">Qualificado</option>
              <option value="nurture">Nutrição</option>
            </select>
            <select value={status} onChange={(e) => setFilter('status', e.target.value)} className="field field--select field--lg lg:w-40 lg:py-2">
              <option value="">Todos os status</option>
              {(Object.keys(STATUS_LABELS) as LeadStatus[]).map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
            <label className="inline-flex items-center gap-2 py-1 text-sm text-gray-700 whitespace-nowrap">
              <input type="checkbox" checked={overdue} onChange={(e) => setFilter('overdue', e.target.checked ? '1' : '')} className="check-input lg:h-4 lg:w-4" />
              Só atrasados (prazo)
            </label>
            <label className="inline-flex items-center gap-2 py-1 text-sm text-gray-700 whitespace-nowrap">
              <input type="checkbox" checked={notJoined} onChange={(e) => setFilter('notJoined', e.target.checked ? '1' : '')} className="check-input lg:h-4 lg:w-4" />
              Não entrou no grupo
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-3">{total} lead(s)</p>
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('common.loading')}</p>
            </div>
          ) : leads.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <ListChecks className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900">Nenhum lead encontrado</h3>
              <p className="text-sm text-gray-500 mt-1">Ative o quiz numa landing page para começar a receber leads.</p>
            </div>
          ) : (
            leads.map((lead) => {
              const deadline = sdrDeadline(lead);
              const waDigits = lead.whatsapp.replace(/\D/g, '');
              const isOpen = expanded === lead._id;
              const nurture = nurtureLabel(lead);
              return (
                <div key={lead._id} className={`bg-white rounded-lg shadow ${deadline?.overdue ? 'ring-1 ring-red-300' : ''}`}>
                  <div className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-gray-900">{lead.name}</span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full inline-flex items-center gap-1 ${OUTCOME_BADGE[lead.outcome].cls}`}>
                          {lead.outcome === 'vip' && <Star className="w-3 h-3" />}
                          {OUTCOME_BADGE[lead.outcome].label}
                        </span>
                        {lead.groupJoin?.token && (
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full inline-flex items-center gap-1 ${lead.groupJoin.clickedAt ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}
                            title={lead.groupJoin.clickedAt ? `Clicou para entrar em ${formatDateTime(lead.groupJoin.clickedAt)}` : 'Recebeu o link e ainda não clicou'}
                          >
                            <Users className="w-3 h-3" /> {lead.groupJoin.clickedAt ? 'Entrou no grupo' : 'Não entrou no grupo'}
                          </span>
                        )}
                        {deadline && (
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full inline-flex items-center gap-1 ${deadline.overdue ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                            <Clock className="w-3 h-3" /> {deadline.text}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        <span>{formatPhone(lead.whatsapp)}</span>
                        <span className="truncate">{lead.email}</span>
                        <span>/l/{lead.landingPage?.slug || lead.landingPageSlugSnapshot}</span>
                        <span>{formatDateTime(lead.createdAt)}</span>
                        {nurture && <span className="text-blue-700">{nurture}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 border-t border-gray-100 pt-3 md:flex-row md:items-center md:border-0 md:pt-0">
                      <div className="grid grid-cols-2 gap-2 md:flex">
                        <select
                          value=""
                          disabled={moving === lead._id}
                          onChange={(e) => e.target.value && moveLead(lead, e.target.value as Outcome)}
                          className="field field--select md:w-auto min-h-[44px] md:min-h-0 md:px-2 md:py-1.5 disabled:opacity-50"
                          aria-label="Mover para outro grupo"
                          title="Mover para outro grupo"
                        >
                          <option value="">Mover para…</option>
                          {(['vip', 'qualified', 'nurture'] as Outcome[]).filter((o) => o !== lead.outcome).map((o) => (
                            <option key={o} value={o}>{OUTCOME_BADGE[o].label}</option>
                          ))}
                        </select>
                        <select
                          value={lead.status}
                          onChange={(e) => updateLead(lead, { status: e.target.value as LeadStatus })}
                          className="field field--select md:w-auto min-h-[44px] md:min-h-0 md:py-1.5"
                          aria-label="Status do atendimento"
                        >
                          {(Object.keys(STATUS_LABELS) as LeadStatus[]).map((s) => (
                            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                          ))}
                        </select>
                      </div>
                      <div className={`grid ${waDigits ? 'grid-cols-5' : 'grid-cols-4'} gap-2 md:flex md:gap-1`}>
                        {waDigits && (
                          <ActionButton
                            action="whatsapp"
                            href={waLink(waDigits, messageFor(lead))}
                            onClick={() => markContacted(lead)}
                          />
                        )}
                        <ActionButton
                          action="email"
                          href={`mailto:${lead.email}`}
                        />
                        <ActionButton
                          icon={FileText}
                          label="Orçamento"
                          variant="primary"
                          onClick={() => createQuote(lead)}
                        />
                        <ActionButton
                          action={isOpen ? 'collapse' : 'expand'}
                          expanded={isOpen}
                          onClick={() => setExpanded(isOpen ? null : lead._id)}
                        />
                        <ActionButton
                          action="delete"
                          onClick={() => deleteLead(lead)}
                        />
                      </div>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="border-t border-gray-100 p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">Respostas</h4>
                        <dl className="space-y-1.5 text-sm">
                          {lead.answers.map((a) => (
                            <div key={a.questionKey}>
                              <dt className="text-gray-500">{a.questionText}</dt>
                              <dd className="font-medium text-gray-900">{a.optionLabel}</dd>
                            </div>
                          ))}
                        </dl>
                        {(lead.outcomeHistory || []).length > 0 && (
                          <p className="text-xs text-gray-500 mt-3 inline-flex items-start gap-1">
                            <ArrowRightLeft className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>
                              {(lead.outcomeHistory || []).map((h) => `${OUTCOME_BADGE[h.from]?.label || h.from} → ${OUTCOME_BADGE[h.to]?.label || h.to} (${formatDateTime(h.at)})`).join(' · ')}
                            </span>
                          </p>
                        )}
                        {(lead.utm?.source || lead.utm?.campaign) && (
                          <p className="text-xs text-gray-500 mt-3">
                            Origem: {[lead.utm?.source, lead.utm?.campaign].filter(Boolean).join(' / ')}
                          </p>
                        )}
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">Notas do SDR</h4>
                        <textarea
                          rows={4}
                          value={notesDraft[lead._id] ?? lead.notes}
                          onChange={(e) => setNotesDraft((prev) => ({ ...prev, [lead._id]: e.target.value }))}
                          className="field"
                          placeholder="Ex.: ligou dia 12, pediu proposta para família de 4"
                        />
                        <Button size="sm" className="w-full sm:w-auto mt-2"
                          type="button"
                          disabled={(notesDraft[lead._id] ?? lead.notes) === lead.notes}
                          onClick={() => updateLead(lead, { notes: notesDraft[lead._id] ?? lead.notes })}>
                          Salvar notas
                        </Button>
                      </div>
                      <div className="md:col-span-2">
                        <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2 inline-flex items-center gap-1">
                          <Sparkles className="w-3.5 h-3.5" /> Primeira mensagem (IA)
                        </h4>
                        {lead.firstMessage?.status === 'pending' && !messageFor(lead) ? (
                          <p className="text-sm text-gray-500">Gerando a mensagem… recarregue em alguns segundos.</p>
                        ) : (
                          <>
                            {lead.firstMessage?.status === 'failed' && (
                              <p className="text-sm text-red-600 mb-2">Não foi possível gerar: {lead.firstMessage.error || 'erro desconhecido'}</p>
                            )}
                            {(messageFor(lead) || lead.firstMessage?.status === 'ready') && (
                              <textarea
                                rows={6}
                                value={messageFor(lead)}
                                onChange={(e) => setMessageDraft((prev) => ({ ...prev, [lead._id]: e.target.value }))}
                                className="field"
                              />
                            )}
                          </>
                        )}
                        <div className="grid grid-cols-1 gap-2 mt-2 sm:flex sm:flex-wrap">
                          {waDigits && messageFor(lead) && (
                            <Button size="sm"
                              href={waLink(waDigits, messageFor(lead))}
                              onClick={() => markContacted(lead)}
                              target="_blank"
                              rel="noopener noreferrer">
                              <MessageCircle className="w-4 h-4" /> Abrir no WhatsApp
                            </Button>
                          )}
                          {messageFor(lead) && (
                            <Button variant="ghost" size="sm"
                              type="button"
                              onClick={() => copyMessage(lead)}>
                              <Copy className="w-4 h-4" /> Copiar
                            </Button>
                          )}
                          <Button variant="ghost" size="sm"
                            type="button"
                            disabled={generating === lead._id}
                            onClick={() => generateMessage(lead)}>
                            <RefreshCw className={`w-4 h-4 ${generating === lead._id ? 'animate-spin' : ''}`} />
                            {messageFor(lead) ? 'Gerar de novo' : 'Gerar mensagem'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLandingQuizLeads;
